import React from 'react';
import {
  getTierForStatus,
  getLabelForStatus,
  TIER_CLASSES,
  TIER_ICONS,
} from '../../utils/statusConfig';

/**
 * Universal StatusBadge component.
 *
 * This is the ONLY way to render a status badge in the HR Portal.
 * Do NOT create local StatusBadge components or use inline Tailwind for status colors.
 *
 * Pills are always a single horizontal line — never wrap label text.
 *
 * @param {string}  status    - Status key (e.g. 'approved', 'pending', 'rejected')
 * @param {string}  [label]   - Optional label override (default: auto-generated from status)
 * @param {'md'}    [size]    - Optional 'md' for rare hero/prominent contexts
 * @param {boolean} [showIcon=false] - Show tier icon (opt-in, off by default)
 * @param {React.ComponentType} [icon] - Optional icon. Replaces the tier icon and is shown even when showIcon is false.
 * @param {string} [title] - Hover text. Defaults to the visible label.
 */
export const StatusBadge = ({ status, label, size, showIcon = false, icon: IconOverride = null, title }) => {
  const tier = getTierForStatus(status);
  const displayLabel = label || getLabelForStatus(status);
  const colorClasses = TIER_CLASSES[tier];
  const sizeClasses = size === 'md' ? 'px-3 py-1 text-xs' : 'px-2 py-0.5 text-[11px]';
  const Icon = IconOverride || (showIcon ? TIER_ICONS[tier] : null);

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ring-1 ring-inset whitespace-nowrap ${colorClasses} ${sizeClasses}`}
      title={title ?? displayLabel}
    >
      {Icon && (
        <Icon
          className={`${IconOverride ? 'w-3.5 h-3.5' : 'w-3 h-3'} mr-1 flex-shrink-0`}
          strokeWidth={IconOverride ? 2.25 : undefined}
        />
      )}
      {displayLabel}
    </span>
  );
};

export default StatusBadge;
